import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  BadgeCheck,
  Clock,
  IndianRupee,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";

const handleDownloadPDF = (analysisResult) => {
  try {
    const data = analysisResult?.openAIResponse || analysisResult?.analysisData || {};
    const doc = new jsPDF("p", "pt", "a4");
    let yPos = 40;

    // Title
    doc.setFontSize(16);
    doc.text("Loan Statement Analysis Report", 40, yPos);
    yPos += 40;

    // Customer Identification & Account Profile
    doc.setFontSize(14);
    doc.text("Customer Identification & Account Profile", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    doc.text(`Name: ${data.loanDetails?.customerName || data.customerDetails?.name || data.borrowerName || 'N/A'}`, 40, yPos);
    doc.text(`Loan Account: ${data.loanDetails?.accountNumber || data.loanAccount || data.accountNumber || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Lender: ${data.loanDetails?.lender || data.bankName || data.lenderName || 'N/A'}`, 40, yPos);
    doc.text(`Loan Type: ${data.loanDetails?.loanType || data.productType || data.loanCategory || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Start Date: ${data.loanDetails?.loanStartDate || 'N/A'}`, 40, yPos);
    doc.text(`Sanctioned Amount: Rs ${data.loanDetails?.sanctionedAmount || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Disbursed Amount: Rs ${data.loanDetails?.disbursedAmount || 'N/A'}`, 40, yPos);
    doc.text(`Loan Tenure: ${data.loanDetails?.totalTenure || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Interest Rate: ${data.loanDetails?.interestRate || 'N/A'}%`, 40, yPos);
    doc.text(`EMI Amount: Rs ${data.loanDetails?.emi || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Current Outstanding: Rs ${data.loanDetails?.currentOutstanding || 'N/A'}`, 40, yPos);
    doc.text(`Status: ${data.loanDetails?.status || 'N/A'}`, 300, yPos);
    yPos += 30;

    // Repayment Summary
    doc.setFontSize(14);
    doc.text("Repayment Summary", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    doc.text(`Total EMIs Paid: Rs ${data.repaymentSummary?.totalEmisPaid || 'N/A'}`, 40, yPos);
    doc.text(`Total EMIs Due: Rs ${data.repaymentSummary?.totalEmisDue || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Total Interest Paid: Rs ${data.repaymentSummary?.totalInterestPaid || 'N/A'}`, 40, yPos);
    doc.text(`Total Principal Repaid: Rs ${data.repaymentSummary?.totalPrincipalRepaid || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Prepayments Made: Rs ${data.repaymentSummary?.prepaymentsMade || 'N/A'}`, 40, yPos);
    doc.text(`Outstanding Principal: Rs ${data.repaymentSummary?.outstandingPrincipal || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Overdue Amount: Rs ${data.repaymentSummary?.overdueAmount || '0'}`, 40, yPos);
    yPos += 30;

    // DPD & Default Behaviour
    doc.setFontSize(14);
    doc.text("DPD & Default Behaviour", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    doc.text(`Maximum DPD: ${data.dpdBehaviour?.maximumDpd || 'N/A'}`, 40, yPos);
    doc.text(`EMI Misses: ${data.dpdBehaviour?.emiMisses || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Recent DPD: ${data.dpdBehaviour?.recentDpd || 'N/A'}`, 40, yPos);
    doc.text(`Settlements/Write-offs: ${data.dpdBehaviour?.settlementsWriteoffs || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Restructured/Moratorium: ${data.dpdBehaviour?.restructuredMoratorium || 'N/A'}`, 40, yPos);
    doc.text(`NPA Status: ${data.dpdBehaviour?.npaStatus || 'N/A'}`, 300, yPos);
    yPos += 30;

    // Fee & Charge Summary
    doc.setFontSize(14);
    doc.text("Fee & Charge Summary", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    doc.text(`Processing Fee: Rs ${data.feeCharges?.processingFee || 'N/A'}`, 40, yPos);
    doc.text(`Foreclosure Charges: Rs ${data.feeCharges?.foreclosureCharges || '0'}`, 300, yPos);
    yPos += 15;
    doc.text(`Insurance Premium: Rs ${data.feeCharges?.insurancePremium || 'N/A'}`, 40, yPos);
    doc.text(`Prepayment Allowed: ${data.feeCharges?.prepaymentAllowed || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Bounce Charges: Rs ${data.feeCharges?.bounceCharges || '0'}`, 40, yPos);
    doc.text(`Penal Charges: Rs ${data.feeCharges?.penalCharges || '0'}`, 300, yPos);
    yPos += 30;

    // Final Credit Analyst Verdict
    doc.setFontSize(14);
    doc.text("Final Credit Analyst Verdict", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    doc.text(`Loan Performance: ${data.finalVerdict?.loanPerformance || 'N/A'}`, 40, yPos);
    doc.text(`Repayment Capacity: ${data.finalVerdict?.repaymentCapacity || 'N/A'}`, 300, yPos);
    yPos += 15;
    doc.text(`Closure Probability: ${data.finalVerdict?.closureProbability || 'N/A'}`, 40, yPos);
    yPos += 30;

    // Final Comments
    doc.setFontSize(14);
    doc.text("Final Comments", 40, yPos);
    yPos += 20;
    doc.setFontSize(10);
    const comments = data.finalComments || [
      "Loan statement analysis completed based on available payment data.",
      "Repayment behavior and DPD patterns have been assessed.",
      "Loan performance evaluation completed for credit assessment.",
      "Recommendation based on payment history and loan management."
    ];
    comments.forEach(comment => {
      doc.text(`• ${comment}`, 40, yPos);
      yPos += 15;
    });

    doc.save("loan-statement-analysis-report.pdf");
  } catch (error) {
    console.error('PDF generation error:', error);
    alert(`Error generating PDF: ${error.message}`);
  }
};

const LoanStatementAnalysisReport = ({ analysisResult }) => {
  const data =
    analysisResult?.openAIResponse || analysisResult?.analysisData || {};

  const getValue = (path, defaultValue = "N/A") => {
    return (
      path?.split(".").reduce((obj, key) => obj?.[key], data) || defaultValue
    );
  };
  return (
    <div className="bg-[#131313] text-white p-6 text-sm space-y-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="w-full sm:max-w-sm">
          <label className="block mb-1 text-sm">Select Analyzer Type</label>
          <select className="w-full bg-[#1f1f1f] border border-[#333] rounded-md px-4 py-2 text-white">
            <option>Universal Loan Statement Analyser</option>
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
         
          <button
            onClick={() => handleDownloadPDF(analysisResult)}
            className="bg-[#FFCA20] hover:bg-[#e6b91a] text-black px-4 py-2 rounded-md font-semibold text-sm"
          >
            Download Report
          </button>
        </div>
      </div>

      {/* Customer Profile */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 space-y-2 border border-[#2a2a2a]">
        <h2 className="text-yellow-400 text-lg font-semibold">
          📄 Customer Identification & Account Profile
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Name</strong>
            <span className="text-white">
              {getValue("loanDetails.customerName") ||
                getValue("customerDetails.name") ||
                getValue("borrowerName")}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Loan Account Number</strong>
            <span className="text-white">
              {getValue("loanDetails.accountNumber") ||
                getValue("loanAccount") ||
                getValue("accountNumber")}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Lender Institution</strong>
            <span className="text-white">
              {getValue("loanDetails.lender") ||
                getValue("bankName") ||
                getValue("lenderName")}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Loan Type</strong>
            <span className="text-white">
              {getValue("loanDetails.loanType") ||
                getValue("productType") ||
                getValue("loanCategory")}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Loan Start Date</strong>
            <span className="text-white">
              {data.loanDetails?.loanStartDate || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Sanctioned Amount</strong>
            <span className="text-white">
              ₹{data.loanDetails?.sanctionedAmount?.toLocaleString("en-IN") || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Disbursed Amount</strong>
            <span className="text-white">
              ₹{data.loanDetails?.disbursedAmount?.toLocaleString("en-IN") || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Loan Tenure</strong>
            <span className="text-white">
              {data.loanDetails?.totalTenure || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Interest Rate</strong>
            <span className="text-white">
              {data.loanDetails?.interestRate || "N/A"}%
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">EMI Amount</strong>
            <span className="text-white">
              ₹{data.loanDetails?.emi?.toLocaleString("en-IN") || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Current Outstanding</strong>
            <span className="text-white">
              ₹{data.loanDetails?.currentOutstanding?.toLocaleString("en-IN") || "N/A"}
            </span>
          </div>
          <div className="flex flex-col">
            <strong className="text-[#AAAAAA]">Loan Status</strong>
            <span className="text-green-400">
              {data.loanDetails?.status || "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Repayment Summary */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a] space-y-2">
        <h2 className="text-yellow-400 text-lg font-semibold">
          💳 Repayment Summary
        </h2>
        <div className="space-y-4">
          {/* Row 1 - 3 elements */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:mx-[12.5%]">
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">
                Total EMIs Paid
              </strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.totalEmisPaid?.toLocaleString("en-IN") ||
                  "N/A"}
              </span>
            </div>
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">Total EMIs Due</strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.totalEmisDue?.toLocaleString("en-IN") || "N/A"}
              </span>
            </div>
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">
                Total Interest Paid
              </strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.totalInterestPaid?.toLocaleString("en-IN") ||
                  "N/A"}
              </span>
            </div>
          </div>
          {/* Row 2 - 4 elements */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">
                Total Principal Repaid
              </strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.totalPrincipalRepaid?.toLocaleString("en-IN") ||
                  "N/A"}
              </span>
            </div>
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">
                Prepayments Made
              </strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.prepaymentsMade?.toLocaleString("en-IN") ||
                  "N/A"}
              </span>
            </div>
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">
                Outstanding Principal
              </strong>
              <span className="text-white text-lg font-medium">
                ₹
                {data.repaymentSummary?.outstandingPrincipal?.toLocaleString("en-IN") ||
                  "N/A"}
              </span>
            </div>
            <div className="flex flex-col">
              <strong className="text-[#AAAAAA] text-sm">Overdue Amount</strong>
              <span className="text-white text-lg font-medium">
                ₹{data.repaymentSummary?.overdueAmount?.toLocaleString("en-IN") || "0"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Instalment Payment History */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a]">
        <div className="flex items-center gap-2 text-yellow-400 font-semibold text-lg">
          <Clock size={18} /> Instalment Payment History
        </div>
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full mt-3 text-sm min-w-[600px] border-collapse">
            <thead className="text-[#AAAAAA]">
              <tr>
                <th className="text-left">Month</th>
                <th className="text-left">EMI Due</th>
                <th className="text-left">Paid On</th>
                <th className="text-left">Amount Paid</th>
                <th className="text-left">DPD</th>
                <th className="text-left">Payment Mode</th>
              </tr>
            </thead>
            <tbody className="text-white">
              {(
                data.paymentHistory || [
                  {
                    month: "Jan 2025",
                    emiDue: 28500,
                    paidOn: "05 Jan 2025",
                    amountPaid: 28500,
                    dpd: 0,
                    paymentMode: "Auto",
                  },
                  {
                    month: "Feb 2025",
                    emiDue: 28500,
                    paidOn: "07 Feb 2025",
                    amountPaid: 28500,
                    dpd: 2,
                    paymentMode: "UPI",
                  },
                  {
                    month: "Mar 2025",
                    emiDue: 28500,
                    paidOn: "05 Mar 2025",
                    amountPaid: 28500,
                    dpd: 0,
                    paymentMode: "Auto",
                  },
                  {
                    month: "Apr 2025",
                    emiDue: 28500,
                    paidOn: "05 Apr 2025",
                    amountPaid: 28500,
                    dpd: 0,
                    paymentMode: "Auto",
                  },
                  {
                    month: "May 2025",
                    emiDue: 28500,
                    paidOn: "15 May 2025",
                    amountPaid: 28500,
                    dpd: 10,
                    paymentMode: "Cheque",
                  },
                  {
                    month: "Jun 2025",
                    emiDue: 28500,
                    paidOn: "05 Jun 2025",
                    amountPaid: 28500,
                    dpd: 0,
                    paymentMode: "Auto",
                  },
                ]
              ).map((record, i) => (
                <tr key={i}>
                  <td>{record.month}</td>
                  <td>₹{record.emiDue?.toLocaleString("en-IN")}</td>
                  <td>{record.paidOn}</td>
                  <td>₹{record.amountPaid?.toLocaleString("en-IN")}</td>
                  <td
                    className={
                      record.dpd > 0 ? "text-yellow-400" : "text-green-400"
                    }
                  >
                    {record.dpd}
                  </td>
                  <td>{record.paymentMode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DPD & Default */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a]">
        <div className="flex items-center gap-2 text-yellow-400 font-semibold text-lg">
          <BadgeCheck size={18} /> DPD & Default Behaviour
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Maximum DPD</strong>
              <span className="text-yellow-400">
                {data.dpdBehaviour?.maximumDpd || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">EMI Misses</strong>
              <span className="text-white">
                {data.dpdBehaviour?.emiMisses || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Recent DPD</strong>
              <span className="text-green-400">
                {data.dpdBehaviour?.recentDpd || "N/A"}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">
                Settlements / Write-offs
              </strong>
              <span className="text-white">
                {data.dpdBehaviour?.settlementsWriteoffs || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">
                Restructured / Moratorium
              </strong>
              <span className="text-white">
                {data.dpdBehaviour?.restructuredMoratorium || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">NPA Status</strong>
              <span className="text-white">
                {data.dpdBehaviour?.npaStatus || "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Amortization Snapshot */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a]">
        <div className="flex items-center gap-2 text-yellow-400 font-semibold text-lg">
          <TrendingUp size={18} /> Loan Amortization Snapshot
        </div>
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full mt-3 text-sm min-w-[700px] border-collapse">
            <thead className="text-[#AAAAAA]">
              <tr>
                <th className="text-left">Period</th>
                <th className="text-left">Opening Principal</th>
                <th className="text-left">EMI</th>
                <th className="text-left">Interest</th>
                <th className="text-left">Principal Paid</th>
                <th className="text-left">Closing Principal</th>
              </tr>
            </thead>
            <tbody className="text-white">
              {(
                data.amortizationSchedule || [
                  {
                    period: "Jan 2025",
                    openingPrincipal: 900000,
                    emi: 28500,
                    interest: 7125,
                    principalPaid: 21375,
                    closingPrincipal: 878625,
                  },
                  {
                    period: "Feb 2025",
                    openingPrincipal: 878625,
                    emi: 28500,
                    interest: 6957,
                    principalPaid: 21543,
                    closingPrincipal: 857082,
                  },
                  {
                    period: "Mar 2025",
                    openingPrincipal: 857082,
                    emi: 28500,
                    interest: 6788,
                    principalPaid: 21712,
                    closingPrincipal: 835370,
                  },
                ]
              ).map((record, i) => (
                <tr key={i}>
                  <td>{record.period}</td>
                  <td>₹{record.openingPrincipal?.toLocaleString("en-IN")}</td>
                  <td>₹{record.emi?.toLocaleString("en-IN")}</td>
                  <td className="text-red-400">
                    ₹{record.interest?.toLocaleString("en-IN")}
                  </td>
                  <td className="text-green-400">
                    ₹{record.principalPaid?.toLocaleString("en-IN")}
                  </td>
                  <td>₹{record.closingPrincipal?.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fee & Charges */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a]">
        <div className="flex items-center gap-2 text-yellow-400 font-semibold text-lg">
          <IndianRupee size={18} /> Fee & Charge Summary
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Processing Fee</strong>
              <span className="text-white">
                ₹{data.feeCharges?.processingFee?.toLocaleString("en-IN") || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Foreclosure Charges</strong>
              <span className="text-white">
                ₹{data.feeCharges?.foreclosureCharges?.toLocaleString("en-IN") || "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Insurance Premium</strong>
              <span className="text-white">
                ₹{data.feeCharges?.insurancePremium?.toLocaleString("en-IN") || "N/A"}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Prepayment Allowed</strong>
              <span className="text-white">
                {data.feeCharges?.prepaymentAllowed || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Bounce Charges</strong>
              <span className="text-white">
                ₹{data.feeCharges?.bounceCharges?.toLocaleString("en-IN") || "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <strong className="text-[#AAAAAA]">Penal Charges</strong>
              <span className="text-white">
                ₹{data.feeCharges?.penalCharges?.toLocaleString("en-IN") || "0"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Final Verdict */}
      <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#2a2a2a] text-center">
        <div className="flex justify-center items-center gap-2 text-yellow-400 font-semibold text-lg">
          <ShieldCheck size={18} /> Final Credit Analyst Verdict
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 justify-items-center">
          <div>
            <strong>Loan Performance</strong>
            <br />
            <span className="text-green-400 text-lg">
              {data.finalVerdict?.loanPerformance || "N/A"}
            </span>
          </div>
          <div>
            <strong>Repayment Capacity</strong>
            <br />
            <span className="text-green-400 text-lg">
              {data.finalVerdict?.repaymentCapacity || "N/A"}
            </span>
          </div>
          <div>
            <strong>Closure Probability</strong>
            <br />
            <span className="text-green-400 text-lg">
              {data.finalVerdict?.closureProbability || "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Final Comments */}
      <div className="bg-green-950 border border-green-600 rounded-lg p-4 text-green-400">
        <h3 className="font-semibold text-green-500 mb-2">📝 Final Comments</h3>
        <ul className="list-disc pl-5 space-y-1">
          {data.finalComments?.map((comment, index) => (
            <li key={index}>{comment}</li>
          )) || [
            <li key="default1">
              Loan statement analysis completed based on available payment data.
            </li>,
            <li key="default2">
              Repayment behavior and DPD patterns have been assessed.
            </li>,
            <li key="default3">
              Loan performance evaluation completed for credit assessment.
            </li>,
            <li key="default4">
              Recommendation based on payment history and loan management.
            </li>,
          ]}
        </ul>
      </div>
    </div>
  );
};

export default LoanStatementAnalysisReport;
